import { Link } from 'react-router-dom'
import { ArrowRightIcon, CheckIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

import { PackageCard as PackageCardType } from '../types/package'
import { Button } from './Button'

interface PackageCardProps {
  package: PackageCardType
  className?: string
}

const packageTypeIcons = {
  media: '🎬',
  ai_corporate: '🤖',
  full_stack: '🏠',
  website_hosting: '🌐',
  hardware_bundle: '💻',
}

const packageTypeNames = {
  media: 'Media Package',
  ai_corporate: 'Corporate AI',
  full_stack: 'Full Stack',
  website_hosting: 'Website Hosting',
  hardware_bundle: 'Hardware Bundle',
}

export function PackageCard({ package: pkg, className }: PackageCardProps) {
  const icon = packageTypeIcons[pkg.package_type] || '📦'
  const typeName = packageTypeNames[pkg.package_type] || pkg.package_type

  const getPrimaryPrice = () => {
    if (pkg.billing_type === 'monthly' && pkg.monthly_price_display) {
      return {
        price: pkg.monthly_price_display,
        period: '/month',
      }
    }
    if (pkg.billing_type === 'yearly' && pkg.yearly_price_display) {
      return {
        price: pkg.yearly_price_display,
        period: '/year',
      }
    }
    if (pkg.billing_type === 'one_time' && pkg.one_time_price_display) {
      return {
        price: pkg.one_time_price_display,
        period: '',
      }
    }
    return null
  }

  const primaryPrice = getPrimaryPrice()

  return (
    <div
      className={clsx(
        'bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden',
        pkg.is_featured && 'ring-2 ring-primary-500',
        className
      )}
    >
      {/* Header */}
      <div className="relative">
        {pkg.image_url ? (
          <img
            src={pkg.image_url}
            alt={pkg.name}
            className="w-full h-48 object-cover"
          />
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
            <span className="text-6xl">{icon}</span>
          </div>
        )}

        {/* Badge */}
        {(pkg.badge_text || pkg.is_popular || pkg.is_featured) && (
          <div className="bottom-4 left-4 top-4 right-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-600 text-white">
              {pkg.badge_text || (pkg.is_popular ? 'Popular' : 'Featured')}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Package Type */}
        <div className="flex items-center mb-2">
          <span className="text-sm font-medium text-primary-600">{typeName}</span>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>

        {/* Description */}
        {pkg.short_description && (
          <p className="text-gray-600 mb-4 line-clamp-2">{pkg.short_description}</p>
        )}

        {/* Features */}
        {pkg.key_features && pkg.key_features.length > 0 && (
          <div className="mb-6">
            <ul className="space-y-2">
              {pkg.key_features.slice(0, 3).map((feature, index) => (
                <li key={index} className="flex items-start">
                  <CheckIcon className="w-5 h-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pricing */}
        <div className="mb-6">
          {primaryPrice ? (
            <div className="flex items-baseline">
              <span className="text-3xl font-bold text-gray-900">
                {primaryPrice.price}
              </span>
              {primaryPrice.period && (
                <span className="text-gray-500 ml-1">{primaryPrice.period}</span>
              )}
            </div>
          ) : (
            <div className="text-2xl font-bold text-gray-900">Contact for Pricing</div>
          )}

          {/* Setup fee */}
          {pkg.setup_fee_display && pkg.setup_fee_display !== 'Free' && (
            <div className="text-sm text-gray-500 mt-1">
              + {pkg.setup_fee_display} setup fee
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button
              as={Link}
              to={`/packages/${pkg.slug}`}
              className="flex-1"
              variant="outline"
            >
              View Details
            </Button>
            <Button
              as={Link}
              to={`/packages/${pkg.slug}/customize`}
              className="flex-1"
              variant={pkg.is_featured ? 'primary' : 'outline'}
            >
              Customize
            </Button>
          </div>

          {primaryPrice && (
            <Button
              as={Link}
              to={`/checkout?package=${pkg.slug}`}
              variant="primary"
              className="w-full"
            >
              Get Started
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}